<template>
  <div class="opening-hours">
    <div class="row align-items-center mb-3">
      <div class="col-md-8">
        <h3 class="font-weight-bold mb-0">Opening Hours</h3>
        <div class="text-muted" style="font-size: 13px;">Set service slots, order intervals, delivery/collection cut-offs, order limits and special dates.</div>
      </div>
      <div class="col-md-4 text-right">
        <button class="btn btnSubmit px-4" :disabled="saving" @click="saveChanges">
          {{ saving ? 'Saving...' : 'Save Changes' }}
        </button>
      </div>
    </div>

    <!-- Day selector -->
    <div class="section-card mb-3">
      <h5 class="font-weight-bold mb-0">Days</h5>
      <div class="helper mt-1 mb-4">Select one day, multiple days, or Select All. The Morning, Lunch and Evening settings below will be assigned to the selected day(s). Special dates can be deleted using the &times; that appears on hover, and added at the bottom of the page.</div>
      <div class="d-flex align-items-center flex-wrap mb-2" style="gap:14px">
        <label class="switch-line">
          <label class="switch mr-2 mb-0"><input type="checkbox" :checked="allDaysSelected" @change="toggleSelectAllDays"><span class="slider"></span></label>
          Select All Days
        </label>
      </div>
      <div class="days-grid">
        <div v-for="day in allDays" :key="day.id" class="day-btn-wrap">
          <button
            type="button"
            :class="['day-btn', { active: selectedDayIds.includes(day.id) }]"
            @click="toggleDay(day)"
            >{{ day.name }}</button>
          <button
            v-if="day.weekdays !== 'Yes'"
            type="button"
            class="day-delete"
            title="Delete special date"
            @click.stop="deleteDay(day)"
            >&times;</button>
        </div>
      </div>
      <div class="day-summary mt-2">
        Settings will be applied to: <strong>{{ selectedDayNames || 'No days selected' }}</strong>
      </div>
    </div>

    <!-- Time slots -->
    <div class="section-card mb-3">
      <h5 class="font-weight-bold mb-0">Time Slots</h5>
      <div class="helper mt-1 mb-2">Each slot can have its own opening/closing times, order interval, delivery/collection cut-offs and optional max order quantity.</div>

      <div v-for="slot in formSlots" :key="slot.mealType" class="slot-card" :class="{ inactive: !slot.isOpen }">
        <div class="d-flex align-items-center justify-content-between mb-2">
          <div class="font-weight-bold" :class="'slot-name-' + slot.mealType.toLowerCase()">{{ slot.mealType }}</div>
          <label class="switch-line">
            <label class="switch mr-2"><input type="checkbox" v-model="slot.isOpen"><span class="slider"></span></label>
            Slot Active
          </label>
        </div>

        <div class="slot-grid">
          <div class="field">
            <label>Opening Time</label>
            <input type="time" class="form-control" v-model="slot.openingTime" :disabled="!slot.isOpen">
          </div>
          <div class="field">
            <label>Closing Time</label>
            <input type="time" class="form-control" v-model="slot.closingTime" :disabled="!slot.isOpen">
          </div>
          <div class="field">
            <label>Order Interval</label>
            <select class="form-control" v-model="slot.intervalTime" :disabled="!slot.isOpen">
              <option v-for="value in frequencyOptions" :key="value" :value="value">{{ value }} min</option>
            </select>
          </div>
          <div class="field">
            <label>Maximum Orders Per Slot</label>
            <div class="toggle-line">
              <label class="switch" style="width: 50px;"><input type="checkbox" v-model="slot.maxLimitEnabled" :disabled="!slot.isOpen"><span class="slider"></span></label>
              <input type="number" min="1" class="form-control ml-2" v-model="slot.maxOrderQty" :disabled="!slot.isOpen || !slot.maxLimitEnabled">
            </div>
          </div>

          <div class="field">
            <label>Delivery</label>
            <div class="toggle-line">
              <label class="switch"><input type="checkbox" v-model="slot.deliveryActive" :disabled="!slot.isOpen"><span class="slider"></span></label>
              <span class="ml-2">Accept delivery</span>
            </div>
          </div>
          <div class="field">
            <label>Last Delivery Order</label>
            <input type="time" class="form-control" v-model="slot.deliveryEnd" :disabled="!slot.isOpen || !slot.deliveryActive">
          </div>
          <div class="field">
            <label>Collection</label>
            <div class="toggle-line">
              <label class="switch"><input type="checkbox" v-model="slot.collectionActive" :disabled="!slot.isOpen"><span class="slider"></span></label>
              <span class="ml-2">Accept collection</span>
            </div>
          </div>
          <div class="field">
            <label>Last Collection Order</label>
            <input type="time" class="form-control" v-model="slot.collectionEnd" :disabled="!slot.isOpen || !slot.collectionActive">
          </div>
        </div>
      </div>
    </div>

    <!-- Customer Order Availability -->
    <div class="section-card mb-3">
      <h5 class="font-weight-bold mb-0">Customer Order Availability</h5>
      <div class="helper mt-1 mb-2">Control whether customers can order for today, order in advance, or choose ASAP. Applies to the whole restaurant.</div>

      <div class="option-grid">
        <div class="option-card">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <div class="font-weight-bold">ASAP</div>
              <div class="helper">Show an "ASAP" option for the earliest available order time.</div>
              <div class="helper" v-if="hasAdvanceOrder">Not available while Advance Order is on.</div>
            </div>
            <label class="switch"><input type="checkbox" v-model="hasAsapOrder" :disabled="hasAdvanceOrder"><span class="slider"></span></label>
          </div>
        </div>

        <div class="option-card">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <div class="font-weight-bold">Advance Order</div>
              <div class="helper">Allow customers to place an order for a future date.</div>
            </div>
            <label class="switch"><input type="checkbox" v-model="hasAdvanceOrder"><span class="slider"></span></label>
          </div>
          <div class="d-flex align-items-center mt-2">
            <label class="mb-0 mr-2">Minimum days in advance</label>
            <input type="number" min="0" class="form-control" style="max-width:120px" v-model="minimumDaysInAdvance" :disabled="!hasAdvanceOrder">
          </div>
        </div>
      </div>
    </div>

    <!-- Daily max order -->
    <div class="section-card mb-3">
      <div>
        <h5 class="font-weight-bold mb-0">Maximum Order Quantity Per Day</h5>
        <div class="helper">Turn this on only if you want to limit the total number of orders accepted in a day.</div>
      </div>
      <div class="d-flex align-items-center mt-2" style="gap:10px">
        <label class="switch" style="margin-bottom: -20px;"><input type="checkbox" v-model="hasDailyMaxOrder"><span class="slider"></span></label>
        <div class="field mb-0" style="max-width:220px">
          <label>Maximum Orders Per Day</label>
          <input type="number" min="1" class="form-control" v-model="dailyMaxOrderQty" :disabled="!hasDailyMaxOrder">
        </div>
      </div>
    </div>

    <!-- Special date create -->
    <div class="section-card mb-3">
      <h5 class="font-weight-bold mb-0">Add Special Date</h5>
      <div class="helper mt-1 mb-2">Create a new special date, then select it from the Days list above to edit its Time Slots.</div>
      <div class="add-special-row">
        <input type="date" class="form-control" v-model="specialDate">
        <button type="button" class="btn btnSubmit" @click="addSpecialDate">Add Special Date</button>
      </div>
    </div>
  </div>
</template>
<script>
import axios from 'axios';
import moment from 'moment'

const WEEKDAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const MEAL_TYPES = ['Morning', 'Lunch', 'Evening']

export default {
  data () {
    return {
      restaurantId: localStorage.getItem('restaurantId'),
      jwtToken: this.$cookies.get('token') || null,
      saving: false,

      weekdays: [],
      specialDates: [],
      selectedDayIds: [],

      hasWeekDaysOpen: 'Open',
      formSlots: MEAL_TYPES.map(type => this.defaultFormSlot(type)),

      hasSameDayOrder: false,
      hasAsapOrder: false,
      hasAdvanceOrder: false,
      minimumDaysInAdvance: '1',
      hasDailyMaxOrder: false,
      dailyMaxOrderQty: '200',

      specialDate: '',

      frequencyOptions: [5, 10, 15, 20, 30, 45, 60]
    }
  },
  computed: {
    allDays() {
      return [...this.weekdays, ...this.specialDates]
    },
    allDaysSelected() {
      return this.allDays.length > 0 && this.allDays.every(day => this.selectedDayIds.includes(day.id))
    },
    selectedDayNames() {
      return this.allDays
        .filter(day => this.selectedDayIds.includes(day.id))
        .map(day => day.name)
        .join(', ')
    }
  },
  watch: {
    hasAdvanceOrder(isOn) {
      // advance order and ASAP can not be on at the same time
      if (isOn) this.hasAsapOrder = false
    }
  },
  mounted() {
    this.getTimeSlots()
  },
  methods: {
    defaultFormSlot(mealType) {
      return {
        mealType,
        isOpen: mealType === 'Evening',
        openingTime: '',
        closingTime: '',
        intervalTime: 15,
        deliveryActive: false,
        deliveryEnd: '',
        collectionActive: false,
        collectionEnd: '',
        maxLimitEnabled: false,
        maxOrderQty: ''
      }
    },
    to24(apiTime) {
      return apiTime ? moment(apiTime, 'hh:mm A').format('HH:mm') : ''
    },
    toApiTime(time24) {
      return time24 ? moment(time24, 'HH:mm').format('hh:mm A') : ''
    },
    buildFormSlots(mealOfDays) {
      return MEAL_TYPES.map(type => {
        const meal = (mealOfDays || []).find(m => m.mealType === type)
        if (!meal) return this.defaultFormSlot(type)
        return {
          mealType: type,
          isOpen: meal.hasOpen === 'Open',
          openingTime: this.to24(meal.openingTime),
          closingTime: this.to24(meal.closingTime),
          intervalTime: meal.intervalTime ? Number(meal.intervalTime) : 15,
          deliveryActive: meal.hasDeliveryAvailable === 'Active',
          deliveryEnd: this.to24(meal.deliveryEnd),
          collectionActive: meal.hasCollectionAvailable === 'Active',
          collectionEnd: this.to24(meal.collectionEnd),
          maxLimitEnabled: meal.hasMaxOrderLimit === 'Yes',
          maxOrderQty: meal.maxOrderQty || ''
        }
      })
    },
    buildApiSlots(formSlots) {
      return formSlots.map(slot => ({
        mealType: slot.mealType,
        hasOpen: slot.isOpen ? 'Open' : 'Closed',
        openingTime: this.toApiTime(slot.openingTime),
        closingTime: this.toApiTime(slot.closingTime),
        intervalTime: String(slot.intervalTime || ''),
        hasDeliveryAvailable: slot.deliveryActive ? 'Active' : 'Inactive',
        deliveryEnd: this.toApiTime(slot.deliveryEnd),
        hasCollectionAvailable: slot.collectionActive ? 'Active' : 'Inactive',
        collectionEnd: this.toApiTime(slot.collectionEnd),
        hasMaxOrderLimit: slot.maxLimitEnabled ? 'Yes' : 'No',
        maxOrderQty: String(slot.maxOrderQty || '')
      }))
    },
    getTimeSlots() {
      let url = process.env.VUE_APP_API_URL + '/api/timeSlot?restaurantId=' + this.restaurantId

      axios
        .get(url, { headers: { Authorization: `Bearer ${this.jwtToken}` } })
        .then((data) => {
          if (data.status !== 200) return

          const allDays = data.data
          this.weekdays = allDays
            .filter(day => day.weekdays === 'Yes')
            .sort((a, b) => WEEKDAY_ORDER.indexOf(a.name) - WEEKDAY_ORDER.indexOf(b.name))
          this.specialDates = allDays
            .filter(day => day.weekdays !== 'Yes')
            .sort((a, b) => moment(a.name, 'DD/MM/YYYY').valueOf() - moment(b.name, 'DD/MM/YYYY').valueOf())

          if (!this.allDays.length) return

          const stillSelected = this.selectedDayIds.filter(id => this.allDays.some(day => day.id === id))
          this.selectedDayIds = stillSelected.length ? stillSelected : [this.allDays[0].id]

          if (this.selectedDayIds.length === 1) {
            this.loadDayIntoForm(this.allDays.find(day => day.id === this.selectedDayIds[0]))
          }
        })
    },
    loadDayIntoForm(day) {
      if (!day) return
      this.hasWeekDaysOpen = day.hasWeekDaysOpen || 'Open'
      this.formSlots = this.buildFormSlots(day.mealOfDays)
      this.hasSameDayOrder = day.hasSameDayOrder === 'Yes'
      this.hasAsapOrder = day.hasAsapOrder === 'Yes'
      this.hasAdvanceOrder = day.hasAdvanceOrder === 'Yes'
      this.minimumDaysInAdvance = day.minimumDaysInAdvance || '1'
      this.hasDailyMaxOrder = day.hasDailyMaxOrder === 'Yes'
      this.dailyMaxOrderQty = day.dailyMaxOrderQty || '200'
    },
    toggleDay(day) {
      const index = this.selectedDayIds.indexOf(day.id)
      if (index === -1) {
        this.selectedDayIds.push(day.id)
      } else {
        this.selectedDayIds.splice(index, 1)
      }
      if (this.selectedDayIds.length === 1) {
        this.loadDayIntoForm(this.allDays.find(d => d.id === this.selectedDayIds[0]))
      }
    },
    toggleSelectAllDays(event) {
      this.selectedDayIds = event.target.checked ? this.allDays.map(day => day.id) : []
    },
    saveChanges() {
      if (!this.selectedDayIds.length) {
        this.$toastr.w('Please select at least one day', 'Warning')
        return
      }

      this.saving = true
      const mealOfDaysPayload = this.buildApiSlots(this.formSlots)

      const updatePromises = this.selectedDayIds.map(id => {
        const url = process.env.VUE_APP_API_URL + '/api/timeSlot/v1/updateTimeSlot'
        const data = {
          id,
          mealOfDays: mealOfDaysPayload
        }
        return axios.post(url, data, { headers: { Authorization: `Bearer ${this.jwtToken}` } })
      })

      Promise.all(updatePromises)
        .then(() => this.saveSettingsAllWeekdays())
        .then(() => {
          this.getTimeSlots()
          this.$toastr.s('Updated successfully', 'Success')
        })
        .catch(() => {
          this.$toastr.e('Failed to update time slots', 'Error')
        })
        .finally(() => {
          this.saving = false
        })
    },
    saveSettingsAllWeekdays() {
      const url = process.env.VUE_APP_API_URL + '/api/timeSlot/v1/updateSettingsAllWeekdays'
      const data = {
        restaurantId: this.restaurantId,
        hasSameDayOrder: this.hasSameDayOrder ? 'Yes' : 'No',
        hasAsapOrder: this.hasAsapOrder ? 'Yes' : 'No',
        hasAdvanceOrder: this.hasAdvanceOrder ? 'Yes' : 'No',
        minimumDaysInAdvance: String(this.minimumDaysInAdvance || ''),
        hasDailyMaxOrder: this.hasDailyMaxOrder ? 'Yes' : 'No',
        dailyMaxOrderQty: String(this.dailyMaxOrderQty || '')
      }
      return axios.post(url, data, { headers: { Authorization: `Bearer ${this.jwtToken}` } })
    },

    // Special dates
    addSpecialDate() {
      if (!this.specialDate) {
        this.$toastr.w('Please select a special date', 'Warning')
        return
      }

      const name = moment(this.specialDate).format('DD/MM/YYYY')
      const url = process.env.VUE_APP_API_URL + '/api/timeSlot/create'
      const data = { restaurantId: this.restaurantId, name }

      axios
        .post(url, data, { headers: { Authorization: `Bearer ${this.jwtToken}` } })
        .then(() => {
          this.getTimeSlots()
          this.specialDate = ''
          this.$toastr.s('Special date added successfully', 'Success')
        })
        .catch(() => this.$toastr.e('Failed to add special date', 'Error'))
    },
    deleteDay(day) {
      if (!confirm('Are you sure to delete this special date?')) return

      const url = process.env.VUE_APP_API_URL + '/api/timeSlot/delete?id=' + day.id
      axios
        .get(url, { headers: { Authorization: `Bearer ${this.jwtToken}` } })
        .then(() => {
          this.selectedDayIds = this.selectedDayIds.filter(id => id !== day.id)
          this.getTimeSlots()
          this.$toastr.s('Special date deleted successfully', 'Success')
        })
        .catch(() => this.$toastr.e('Failed to delete special date', 'Error'))
    }
  }
}
</script>
<style scoped>
.opening-hours select, .opening-hours input, .opening-hours .form-control { border-radius: 7px; }
.section-card {
  background-color: white;
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
}
.helper { color: #6f7780; font-size: 13px; }

.days-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(112px, 1fr)); gap: 10px; }
.day-btn-wrap { position: relative; }
.day-btn {
  border: 1px solid #e2dfdc;
  background: #fff;
  border-radius: 8px;
  padding: 10px 6px;
  font-weight: 700;
  color: #343a40;
  cursor: pointer;
  width: 100%;
}
.day-btn.active {
  background: linear-gradient(179.48deg, #FD7902 36.9%, #FF0101 93.77%);
  border-color: transparent;
  color: #fff;
}
.day-delete {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  line-height: 18px;
  padding: 0;
  border: 1px solid #e0e0e0;
  border-radius: 50%;
  background: #fff;
  color: #c63825;
  font-weight: 700;
  cursor: pointer;
  opacity: 0;
  transition: opacity .15s;
}
.day-btn-wrap:hover .day-delete { opacity: 1; }
.day-summary { font-size: 13px; color: #7c4b30; background: #fff8f3; border: 1px solid #f1ded2; border-radius: 8px; padding: 8px 10px; }
.add-special-row { display: flex; align-items: center; gap: 10px; }
.add-special-row input[type=date] { max-width: 200px; }

.slot-card { border: 1px solid #e0e0e0; border-radius: 10px; margin-top: 12px; padding: 14px; background: #fffaf7; }
.slot-card.inactive { opacity: .6; }
.slot-name-morning { color: #f26422; }
.slot-name-lunch { color: #1fa44a; }
.slot-name-evening { color: #6942d9; }
.slot-grid { display: grid; grid-template-columns: repeat(4, minmax(150px, 1fr)); gap: 12px; margin-top: 10px; }
.field label { font-size: 13px; font-weight: 700; color: #3c4249; display: block; margin-bottom: 4px; }
.toggle-line { display: flex; align-items: center; border: 1px solid #e2dfdc; border-radius: 8px; padding: 6px 10px; background: #fff; min-height: 42px; }

.switch-line { display: flex; align-items: center; font-size: 13px; font-weight: 700; color: #3c4249; cursor: pointer; }
.switch { position: relative; width: 42px; height: 24px; display: inline-block; }
.switch input { opacity: 0; width: 0; height: 0; }
.slider { position: absolute; cursor: pointer; inset: 0; background: #c9c9c9; border-radius: 999px; transition: .2s; }
.slider:before { content: ""; position: absolute; height: 18px; width: 18px; left: 3px; top: 3px; background: white; border-radius: 50%; transition: .2s; }
.switch input:checked + .slider { background: #20b35b; }
.switch input:checked + .slider:before { transform: translateX(18px); }
.switch input:disabled + .slider { opacity: .5; cursor: not-allowed; }

.option-grid { display: grid; grid-template-columns: repeat(2, minmax(260px, 1fr)); gap: 14px; margin-top: 12px; }
.option-card { border: 1px solid #e0e0e0; border-radius: 10px; padding: 14px; background: #fffaf7; }

.btnSubmit {
  border: unset;
  font-weight: bold;
  color: #fff;
  border-radius: 5px;
  background: linear-gradient(179.48deg, #FD7902 36.9%, #FF0101 93.77%);
}
.btnSubmit:disabled { opacity: .7; cursor: not-allowed; }

@media (max-width: 1100px) {
  .days-grid { grid-template-columns: repeat(4, 1fr); }
  .slot-grid { grid-template-columns: repeat(2, minmax(150px, 1fr)); }
  .option-grid { grid-template-columns: 1fr; }
}
</style>
